/**
 * Cut home — recent pools tagged from this app + CTA to new cut.
 */

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Scissors } from 'lucide-react';
import { useItemPoolsStore } from '@/stores/prioritization/itemPoolsStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, PrimaryButton, StatusChip } from '../components/primitives';

export default function CutHomeScreen() {
  const navigate = useNavigate();
  const app = getAppDefinition('cut')!;
  const userId = useUserStore((s) => s.userId);
  const pools = useItemPoolsStore((s) => s.itemPools);
  const fetchFromSupabase = useItemPoolsStore((s) => s.fetchFromSupabase);

  useEffect(() => {
    void fetchFromSupabase();
  }, [fetchFromSupabase]);

  const mine = useMemo(() => {
    const tagged = pools.filter(
      (p) =>
        (p.tags ?? []).includes('cut-app') ||
        (userId && p.ownerId === userId && (p.description ?? '').startsWith('Cut kept')),
    );
    return [...tagged].sort(
      (a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime(),
    );
  }, [pools, userId]);

  return (
    <AppShell
      app={app}
      actions={
        <PrimaryButton onClick={() => navigate('/apps/cut/new')}>
          <Plus className="h-4 w-4" /> New cut
        </PrimaryButton>
      }
    >
      <ScreenHeading
        title="Cut"
        subtitle="S2 only — order candidates, draw the cut, save the list as an item pool with the cut line in place."
      />
      <BallLine ballWith="you — draw the cut when the list is ready" />

      {mine.length === 0 ? (
        <EmptyState
          title="No cuts saved yet"
          hint="Bring a list of candidates, put them in order, and keep what is above the line."
          action={
            <PrimaryButton onClick={() => navigate('/apps/cut/new')}>
              <Plus className="h-4 w-4" /> Start a cut
            </PrimaryButton>
          }
        />
      ) : (
        <div className="space-y-3">
          {mine.map((pool) => (
            <AppCard
              key={pool.id}
              onClick={() => navigate(`/prioritization/lists/${pool.id}`)}
            >
              <div className="flex items-center gap-3">
                <Scissors className="h-4 w-4 shrink-0 text-slate-blue-600" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-heading font-semibold text-gray-900 dark:text-[var(--text-pri)]">
                    {pool.name}
                  </p>
                  <p className="mt-0.5 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                    {pool.cutLinePosition ?? 0} kept above the cut,{' '}
                    {Math.max((pool.priorityIds?.length ?? 0) - (pool.cutLinePosition ?? 0), 0)} below
                  </p>
                </div>
                <StatusChip label={pool.status} tone="neutral" />
              </div>
            </AppCard>
          ))}
        </div>
      )}
    </AppShell>
  );
}
